import {useState, useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/dateFormat';
import axios from 'axios';
import PageTitle from '../../components/PageTitle';
import { formatPlanLabel as formatSubscriptionPlanLabel, formatTierLabel } from '../../constants/subscriptionPlans';
import { 
  Gift, 
  Plus, 
  Copy, 
  Filter, 
  RefreshCw, 
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';

interface GiftCard {
  _id: string;
  code: string;
  creditAmount?: number;
  planType?: 'weekly' | 'monthly' | 'quarterly' | 'annual' | string;
  tier?: 'plus' | 'max' | 'ultra';
  status: 'active' | 'redeemed' | 'revoked';
  generatedBy: {
    email: string;
  };
  redeemedBy?: {
    email: string;
  };
  createdAt: string;
  redeemedAt?: string;
  expiresAt?: string;
}

const PLAN_OPTION_KEYS = [
  { value: 'weekly', detailKey: 'common:plan_details.weekly' },
  { value: 'monthly', detailKey: 'common:plan_details.monthly' },
  { value: 'quarterly', detailKey: 'common:plan_details.quarterly' },
  { value: 'annual', detailKey: 'common:plan_details.yearly' },
];

const TIER_OPTION_KEYS = [
  { value: 'plus', detailKey: 'common:tier_detail.plus' },
  { value: 'max', detailKey: 'common:tier_detail.max' },
  { value: 'ultra', detailKey: 'common:tier_detail.ultra' },
];

export default function DashboardGiftCards() {
  const { t } = useTranslation('dashboard');
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateForm, setGenerateForm] = useState<{ planType: string; tier: string; count: number | '' }>({
    planType: PLAN_OPTION_KEYS[0].value,
    tier: TIER_OPTION_KEYS[0].value,
    count: 1
  });
  const [generating, setGenerating] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [cardToRevoke, setCardToRevoke] = useState<GiftCard | null>(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    loadGiftCards();
  }, [currentPage, statusFilter]);

  const loadGiftCards = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(statusFilter && { status: statusFilter })
      });

      const response = await axios.get(`/api/gift-cards?${params}`);
      setGiftCards(response.data.giftCards);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error('Failed to load gift cards:', error);
      toast.error(t('gift_cards.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (generateForm.count === '') return;

    try {
      setGenerating(true);
      await axios.post('/api/gift-cards/generate', {
        planType: generateForm.planType,
        tier: generateForm.tier,
        count: generateForm.count
      });
      
      toast.success(t('gift_cards.generate_success', { count: generateForm.count }));
      setShowGenerateModal(false);
      loadGiftCards();
      // Reset form to default preset
      setGenerateForm({ planType: PLAN_OPTION_KEYS[0].value, tier: TIER_OPTION_KEYS[0].value, count: 1 });
    } catch (error: any) {
      console.error('Failed to generate gift cards:', error);
      toast.error(error.response?.data?.error || t('gift_cards.generate_failed'));
    } finally {
      setGenerating(false);
    }
  };

  const openRevokeModal = (card: GiftCard) => {
    setCardToRevoke(card);
    setShowRevokeModal(true);
  };

  const handleRevoke = async () => {
    if (!cardToRevoke) return;

    try {
      setRevoking(true);
      await axios.delete(`/api/gift-cards/${cardToRevoke._id}`);
      toast.success(t('gift_cards.revoke_success'));
      setShowRevokeModal(false);
      setCardToRevoke(null);
      loadGiftCards();
    } catch (error: any) {
      console.error('Failed to revoke gift card:', error);
      toast.error(error.response?.data?.error || t('gift_cards.revoke_failed'));
    } finally {
      setRevoking(false);
    }
  };

  const copyCode = async (code: string) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        toast.success(t('gift_cards.code_copied'));
      } else {
        // Fallback method for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            toast.success(t('gift_cards.code_copied'));
          } else {
            throw new Error('Copy command failed');
          }
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error('Failed to copy code:', error);
      toast.error(t('gift_cards.copy_failed'));
      // Show the code in an alert as last resort
      prompt(t('gift_cards.copy_fallback'), code);
    }
  };

  const formatCardPlanLabel = (card: GiftCard) => {
    const option = PLAN_OPTION_KEYS.find(opt => opt.value === card.planType);
    if (option) return formatSubscriptionPlanLabel(option.value);
    if (card.planType) return t(`gift_cards.plan_${card.planType}`);
    if (typeof card.creditAmount === 'number') return t('gift_cards.credits', { amount: card.creditAmount.toFixed(2) });
    return t('common:na');
  };

  return (
    <div className="p-8 space-y-6">
      <PageTitle titleKey="gift_cards.title" namespace="dashboard" descriptionKey="gift_cards.subtitle" />
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">{t('gift_cards.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('gift_cards.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowGenerateModal(true)}
          className="inline-flex items-center px-4 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg transition-colors shadow-sm"
        >
          <Plus className="h-5 w-5 mr-2" />
          {t('gift_cards.generate')}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center gap-4">
          <Filter className="h-5 w-5 text-muted-foreground" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border-border rounded-lg bg-muted/50 text-foreground focus:ring-primary focus:border-primary"
          >
            <option value="">{t('gift_cards.all_statuses')}</option>
            <option value="active">{t('gift_cards.active')}</option>
            <option value="redeemed">{t('gift_cards.redeemed')}</option>
            <option value="revoked">{t('gift_cards.revoked')}</option>
          </select>
          <button 
            onClick={() => loadGiftCards()}
            className="ml-auto p-2 text-muted-foreground hover:text-foreground"
            title={t('common:refresh')}
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('gift_cards.col_code')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('gift_cards.col_pass')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('gift_cards.col_tier')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('gift_cards.col_status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('gift_cards.col_generated_by')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('gift_cards.col_redeemed_by')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('gift_cards.col_created')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('gift_cards.col_actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {loading && giftCards.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-muted-foreground">{t('common:loading')}</td>
                </tr>
              ) : giftCards.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-muted-foreground">{t('gift_cards.no_cards')}</td>
                </tr>
              ) : (
                giftCards.map((card) => (
                  <tr key={card._id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <code className="bg-muted px-2 py-1 rounded text-sm font-mono text-foreground">
                          {card.code}
                        </code>
                        <button 
                          onClick={() => copyCode(card.code)}
                          className="text-muted-foreground hover:text-primary"
                          title={t('gift_cards.copy_code')}
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-foreground">
                          {formatCardPlanLabel(card)}
                        </span>
                        {card.planType && (
                          <span className="text-xs text-muted-foreground">
                            {card.planType === 'weekly' && t('common:plans.day_pass_7')}
                            {card.planType === 'monthly' && t('common:plans.day_pass_30')}
                            {card.planType === 'quarterly' && t('common:plans.day_pass_90')}
                            {card.planType === 'annual' && t('common:plans.day_pass_365')}
                            {!['weekly', 'monthly', 'quarterly', 'annual'].includes(card.planType) && t('common:plans.custom_pass')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {card.tier ? (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          card.tier === 'ultra'
                            ? 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400'
                            : card.tier === 'max'
                            ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                            : 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400'
                        }`}>
                          {formatTierLabel(card.tier)}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        card.status === 'active' 
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                          : card.status === 'redeemed'
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400'
                          : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400'
                      }`}>
                        {card.status === 'active' && <CheckCircle className="h-3 w-3" />}
                        {card.status === 'redeemed' && <Gift className="h-3 w-3" />}
                        {card.status === 'revoked' && <XCircle className="h-3 w-3" />}
                        {t(`gift_cards.status_${card.status}`)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {card.generatedBy?.email || t('common:unknown')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {card.redeemedBy ? (
                        <div className="flex flex-col">
                          <span>{card.redeemedBy.email}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(card.redeemedAt!)}
                          </span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatDate(card.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {card.status === 'active' && (
                        <button
                          onClick={() => openRevokeModal(card)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-red-50 text-red-700 hover:bg-red-600 hover:text-white dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-600 dark:hover:text-white border-2 border-red-300 dark:border-red-800 hover:border-red-600 shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105"
                          title={t('gift_cards.revoke_title')}
                        >
                          <XCircle className="h-4 w-4" />
                          <span>{t('gift_cards.revoke')}</span>
                        </button>
                      )}
                      {card.status !== 'active' && (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-muted text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {card.status === 'redeemed' ? t('gift_cards.redeemed') : t('gift_cards.revoked')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="bg-card px-4 py-3 border-t border-border flex items-center justify-between sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50"
            >
              {t('common:previous')}
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50"
            >
              {t('common:next')}
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-foreground">
                {t('common:page_of', { current: currentPage, total: totalPages })}
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label={t('common:previous')}>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border bg-card text-sm font-medium text-muted-foreground hover:bg-muted/50 disabled:opacity-50"
                >
                  {t('common:previous')}
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border bg-card text-sm font-medium text-muted-foreground hover:bg-muted/50 disabled:opacity-50"
                >
                  {t('common:next')}
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto" 
          aria-labelledby="modal-title" 
          role="dialog" 
          aria-modal="true"
          onKeyDown={e => e.key === 'Escape' && !generating && setShowGenerateModal(false)}
          tabIndex={-1}
        >
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              aria-hidden="true"
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            {/* Modal Panel */}
            <div className="inline-block align-bottom bg-card border border-border rounded-xl text-left overflow-hidden shadow-lg transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="bg-card px-6 pt-6 pb-4 border-b border-border">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-14 w-14 rounded-lg bg-primary shadow-xs">
                    <Gift className="h-7 w-7 text-primary-foreground" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-foreground" id="modal-title">
                      {t('gift_cards.generate_title')}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('gift_cards.generate_subtitle')}
                    </p>
                  </div>
                  {!generating && (
                    <button
                      type="button"
                      onClick={() => setShowGenerateModal(false)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <XCircle className="h-6 w-6" />
                    </button>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-6">
                {/* Pass Type Selection */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-3">
                    {t('gift_cards.select_pass')}
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {PLAN_OPTION_KEYS.map((plan) => (
                      <button
                        key={plan.value}
                        type="button"
                        onClick={() => setGenerateForm({ ...generateForm, planType: plan.value })}
                        disabled={generating}
                        className={`relative px-5 py-4 rounded-lg border font-medium text-left transition-colors ${
                          generateForm.planType === plan.value
                            ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                            : 'border-border hover:border-primary/40 bg-card'
                        } ${generating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {generateForm.planType === plan.value && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="h-5 w-5 text-primary" />
                          </div>
                        )}
                        <div className="flex flex-col gap-1">
                          <span className={`text-base font-bold ${
                            generateForm.planType === plan.value
                              ? 'text-primary'
                              : 'text-foreground'
                          }`}>
                            {formatSubscriptionPlanLabel(plan.value)}
                          </span>
                          <span className={`text-xs ${
                            generateForm.planType === plan.value
                              ? 'text-primary'
                              : 'text-muted-foreground'
                          }`}>
                            {t(plan.detailKey)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tier Selection */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-3">
                    {t('gift_cards.select_tier')}
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {TIER_OPTION_KEYS.map((tier) => (
                      <button
                        key={tier.value}
                        type="button"
                        onClick={() => setGenerateForm({ ...generateForm, tier: tier.value })}
                        disabled={generating}
                        className={`relative px-5 py-4 rounded-lg border font-medium text-left transition-colors ${
                          generateForm.tier === tier.value
                            ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                            : 'border-border hover:border-primary/40 bg-card'
                        } ${generating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {generateForm.tier === tier.value && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="h-5 w-5 text-primary" />
                          </div>
                        )}
                        <div className="flex flex-col gap-1">
                          <span className={`text-base font-bold ${
                            generateForm.tier === tier.value
                              ? 'text-primary'
                              : 'text-foreground'
                          }`}>
                            {formatTierLabel(tier.value)}
                          </span>
                          <span className={`text-xs ${
                            generateForm.tier === tier.value
                              ? 'text-primary'
                              : 'text-muted-foreground'
                          }`}>
                            {t(tier.detailKey)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Number of Cards */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    {t('gift_cards.num_cards')}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={generateForm.count}
                      onChange={(e) => {
                        if (e.target.value === '') {
                          setGenerateForm({ ...generateForm, count: '' });
                          return;
                        }

                        const value = parseInt(e.target.value, 10);
                        if (Number.isNaN(value)) return;
                        const clampedValue = Math.min(100, Math.max(1, value));
                        setGenerateForm({ ...generateForm, count: clampedValue });
                      }}
                      disabled={generating}
                      className="block w-full px-4 py-3 border border-border rounded-lg shadow-xs focus:ring-2 focus:ring-primary/30 focus:border-primary text-base font-medium bg-card text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      placeholder={t('gift_cards.num_placeholder')}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <span className="text-xs text-muted-foreground font-medium">
                        {t('gift_cards.max_hint')}
                      </span>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t('gift_cards.max_help')}
                  </p>
                </div>

                {/* Summary Card */}
                <div className="bg-muted/30 border border-border rounded-lg p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-lg bg-primary shadow-xs">
                      <Gift className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-foreground mb-2">
                        {t('gift_cards.summary')}
                      </p>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">{t('gift_cards.quantity')}</span>
                          <span className="text-base font-bold text-foreground">
                            {t('gift_cards.cards_count', { count: generateForm.count })}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">{t('gift_cards.pass_type')}</span>
                          <span className="text-base font-bold text-foreground">
                            {formatSubscriptionPlanLabel(generateForm.planType) || t('common:custom')}
                          </span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-border">
                          <p className="text-xs text-muted-foreground">
                            {t('gift_cards.each_card_info')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="bg-muted/50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  disabled={generating}
                  className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-2.5 border border-border shadow-xs text-sm font-semibold rounded-lg text-foreground bg-card hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generating || generateForm.count === '' || generateForm.count < 1 || generateForm.count > 100}
                  className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-8 py-2.5 text-sm font-semibold rounded-lg text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {generating ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      {t('gift_cards.generating')}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      {t('gift_cards.generate_cards', { count: generateForm.count })}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Confirmation Modal */}
      {showRevokeModal && cardToRevoke && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto"
          onKeyDown={e => e.key === 'Escape' && !revoking && setShowRevokeModal(false)}
          tabIndex={-1}
        >
          <div className="flex items-center justify-center min-h-screen px-4 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity" 
              aria-hidden="true"
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            {/* Modal */}
            <div className="inline-block align-bottom bg-card border border-border rounded-xl text-left overflow-hidden shadow-lg transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              {/* Header with Icon */}
              <div className="bg-card px-6 pt-6 pb-4 border-b border-border">
                <div className="flex items-center">
                  <div className="flex-shrink-0 flex items-center justify-center h-14 w-14 rounded-full bg-red-100 dark:bg-red-900/50 ring-8 ring-red-50 dark:ring-red-900/30">
                    <AlertTriangle className="h-7 w-7 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-bold text-foreground">
                      {t('gift_cards.revoke_modal_title')}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('gift_cards.revoke_warning')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-5">
                <div className="space-y-4">
                  <p className="text-sm text-foreground">
                    {t('gift_cards.revoke_confirm')}
                  </p>

                  {/* Gift Card Details */}
                  <div className="bg-muted/50 rounded-lg p-4 space-y-3 border border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">{t('gift_cards.code_label')}</span>
                      <code className="bg-muted px-3 py-1 rounded text-sm font-mono text-foreground">
                        {cardToRevoke.code}
                      </code>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">{t('gift_cards.pass_label')}</span>
                      <span className="text-lg font-bold text-foreground">
                        {formatCardPlanLabel(cardToRevoke)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">{t('gift_cards.status_label')}</span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                        <CheckCircle className="h-3 w-3" />
                        {t('gift_cards.active')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">{t('gift_cards.created_label')}</span>
                      <span className="text-sm text-foreground">
                        {formatDate(cardToRevoke.createdAt)}
                      </span>
                    </div>
                  </div>

                  {/* Warning Message */}
                  <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-600 p-4 rounded-r">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <XCircle className="h-5 w-5 text-red-400 dark:text-red-500" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800 dark:text-red-300">
                          {t('gift_cards.revoke_note')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="bg-muted/50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowRevokeModal(false)}
                  disabled={revoking}
                  className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-2.5 border border-border shadow-sm text-sm font-medium rounded-lg text-foreground bg-card hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleRevoke}
                  disabled={revoking}
                  className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-8 py-2.5 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {revoking ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      {t('common:processing')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      {t('gift_cards.ok_revoke')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
